/**
 * Configuration mapping file types to their suffix attribute names.
 * Each file type can specify which property attribute should be used as a name suffix.
 * Format: { fileType: { name: 'AttributeName', category: 'CategoryName' } }
 * The category field is optional - if omitted, only the attribute name is matched.
 */
export const SUFFIX_ATTRIBUTE_MAP = {
  'rvt': {
    name: 'ElementId',
    category: '__revit__'
  },
  'dwg': {
    name: 'Handle',
    category: 'General'
  }
};

/**
 * A Revit UniqueId is "<guid>-<elementId in hex>", e.g.
 * "359e1154-f1af-42d6-a495-cf8ce5e309f3-005eff65".
 *
 * We match the whole shape rather than just checking the length, because
 * family type externalIds like "Sink-Offset-Kohler-Vault-3823_4:Plumbing
 * Fixtures" are also 45+ characters and would otherwise be accepted. Those
 * carry no element id at all, and parseInt() reads a bogus one out of their
 * last dash-separated segment - for "M_Concrete-Round-Column with Drop
 * Caps:Structural Columns" it returns 12, the leading "C" of "Column".
 */
const REVIT_UNIQUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-([0-9a-f]+)$/i;

/**
 * Gets the suffix value for a name, trying the property first and falling back to parsing externalId for RVT.
 *
 * @param {PropertyDatabase} pdb - The property database instance
 * @param {number} dbId - The database id
 * @param {number} nameSuffixAttrId - The cached attribute ID for the suffix property
 * @param {number|undefined} nameSuffixValId - The value ID if the suffix property exists on this object
 * @param {string} fileType - The file type ('rvt', 'dwg', etc.)
 * @param {string|undefined} externalId - Optional externalId (if already available)
 * @returns {string} - The suffix value or empty string if not found
 */
export function getSuffixValue(pdb, dbId, nameSuffixAttrId, nameSuffixValId, fileType, externalId) {
  let suffix = "";

  // First try to get suffix from the property
  if (nameSuffixValId !== undefined && nameSuffixAttrId) {
    suffix = pdb.getAttrValue(nameSuffixAttrId, nameSuffixValId);
  }

  // Fallback for RVT: parse ElementId from externalId if property not found
  //This happens for very old Revit translations, where ElementId is not exported,
  //and in all cases for Revit Views where the extractor doesn't output their ElementId separately.
  if (!suffix && fileType === 'rvt') {
    // If externalId not provided, try to load it
    if (externalId === undefined && pdb.externalIdsLoaded()) {
      externalId = pdb.getIdAt(dbId);
    }

    // Parse the ElementId out of the UniqueId. Elements coming from a
    // linked model carry two UniqueIds separated by a slash - the link
    // instance first, the linked element second - so the id we want is
    // always in the last segment.
    if (externalId) {
      const match = REVIT_UNIQUE_ID.exec(externalId.split('/').pop());
      if (match) {
        const eid = parseInt(match[1], 16);
        if (!isNaN(eid)) {
          suffix = eid;
        }
      }
    }
  }

  return String(suffix);
}

/**
 * Appends suffix to a name if suffix exists.
 * The suffix is added in square brackets after the name.
 *
 * @param {string} name - The base name
 * @param {string|null} suffix - The suffix to append
 * @returns {string} - The name with suffix appended in brackets, or original name
 */
export function appendSuffixToName(name, suffix) {
  // Check if name already contains this suffix
  // AutoCAD uses uppercase hex when constructing name, but writes lower case hex in the dedicated property
  const suffixPattern = `[${suffix.toUpperCase()}]`;
  // name could be anything else than a string (e.g. a number)
  if (name && String(name).includes(suffixPattern)) {
    return name;
  }

  // Append suffix
  return name ? `${name} [${suffix}]` : `[${suffix}]`;
}